import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Optional,
  Output,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import {
  IImageUploadResponse,
  SharedRichTextEditorService,
  UploadResult,
} from '@g99-product-web-ui/shared-service';
import { normalizeVariableToken } from '@g99-product-web-ui/shared-util-lib';
import {
  RTEImageTool,
  RTEPreset,
  RichTextEditorService,
} from 'angular-rich-text-editor';
import { Observable, map } from 'rxjs';

@Component({
  selector: 'lib-g99-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class RichTextEditorComponent
  implements OnInit, ControlValueAccessor, Validator
{
  // 💡 Common Inputs
  @Input() public label = '';
  @Input() public configType?: RTEPreset;
  @Input() public initialContent = '';
  @Input() public readOnly = false;

  @Input() public formControls!: AbstractControl;
  @Input() public errorMessage: { [key: string]: string } = {};

  @Input() public imageToolbarItems: Array<RTEImageTool | '/'> = [
    'menu_controljustify',
    'imagestyle',
    'delete',
  ];
  @Input() public excludedToolbarItems: string[] = [
    'paragraphs',
    'html2pdf',
    'strike',
    'toggle_paragraphop',
    'menu_paragraphop',
    'insertcode',
    'insertemoji',
    'insertchars',
    'insertgallery',
    'insertvideo',
    'insertdocument',
    'superscript',
    'subscript',
    'inlinestyle',
    'newdoc',
    'save',
    'print',
    'load',
    'help',
  ];
  @Input() public enableImageUpload = true;
  @Input() public enableVideoEmbed = false;

  // 🔁 Two-way binding for template-driven forms
  @Input() public ngModel = '';
  @Input() public fileUploaderFn?: (file: File) => Observable<string>;
  @Input() public required = false;

  @Output() public ngModelChange = new EventEmitter<string>();

  @Output() public contentChanged = new EventEmitter<void>();
  @Output() public blurEvent = new EventEmitter<void>();

  public value = '';
  public isInvalid = false;
  protected _isTemplateDriven = false;

  private _ngControl: NgControl | null = null;

  constructor(
    private readonly _injector: Injector,
    private readonly _richTextEditorService: RichTextEditorService,
    @Optional()
    private readonly _defaultUploaderService?: SharedRichTextEditorService
  ) {}

  ngOnInit(): void {
    this._ngControl = this._injector.get(NgControl, null, { optional: true });

    if (this._ngControl?.control) {
      this._ngControl.valueAccessor = this;
    }

    // Determine if template-driven (i.e. no FormGroup around it)
    this._isTemplateDriven = !this._ngControl?.control;
  }

  public get getConfigType(): RTEPreset {
    return this.configType || 'FULL';
  }
  public get safeFormControl(): FormControl | null {
    const ctrl = this.formControls || this._ngControl?.control;
    return ctrl instanceof FormControl ? ctrl : null;
  }

  public writeValue(val: string | null | undefined): void {
    this.value = val || this.initialContent || '';
    this.ngModel = this.value;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: (value: string) => void = () => {};

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {};

  public onContentChanged(html: string): void {
    this.value = html;

    if (!this._isTemplateDriven) {
      this.onChange(html); // 🔁 reactive
    } else {
      this.ngModel = html;
      this.ngModelChange.emit(html); // 🔁 template-driven
    }

    this.contentChanged.emit();
  }

  public onBlur(): void {
    this.onTouched();
    this.blurEvent.emit();
  }

  public validate(): ValidationErrors | null {
    const container = document.createElement('div');
    container.innerHTML = this.value || '';

    const text = container.textContent?.trim() || '';
    const hasImage = container.querySelector('img') !== null;
    const hasVideo = container.querySelector('video') !== null;

    const isEmpty = text.length === 0 && !hasImage && !hasVideo;

    // Get the form control instance
    const control = this.safeFormControl;

    // 💡 Only run this check if the control has a `required` validator
    const hasRequiredValidator =
      this.required || control?.hasValidator?.(Validators.required);

    if (!hasRequiredValidator) {
      return null; // No need to run our custom required logic
    }

    return isEmpty ? { required: true } : null;
  }

  public insertVariable(variable: string): void {
    let formatted: string;
    if (variable === '${BUSINESS_LOGO}') {
      formatted = `<img src="${variable}" width="250" height="120" />`;
    } else {
      const safeVariable = normalizeVariableToken(variable);
      formatted = `<span contenteditable="false">${safeVariable}</span>`;
    }
    this._richTextEditorService.insertContentAtCursor(formatted);
    this.contentChanged.emit();
  }

  public getErrorMessage(): string | null {
    const errors = this.formControls?.errors;
    if (!errors) {
      return null;
    }
    const firstKey = Object.keys(errors)[0];
    return this.errorMessage[firstKey] || 'This field is required';
  }

  public get safeUploadHandler() {
    return (
      file: File,
      callback: (url: string | null, errorCode?: string) => void
    ): void => {
      let uploaderFn:
        | ((file: File) => Observable<IImageUploadResponse>)
        | undefined;

      if (this.fileUploaderFn) {
        const fileUploader = this.fileUploaderFn;
        uploaderFn = (f: File) =>
          fileUploader(f).pipe(
            map((res: UploadResult) =>
              typeof res === 'string' ? { location: res } : res
            )
          );
      } else if (this._defaultUploaderService) {
        const service = this._defaultUploaderService;
        uploaderFn = (f: File) => service.uploadImage(f); // ✅ returns Observable
      }

      if (!uploaderFn) {
        callback(null, 'no-uploader');
        return;
      }

      uploaderFn(file).subscribe({
        next: (res: IImageUploadResponse) => {
          const url = res?.location ?? null;
          callback(url);
        },
        error: () => {
          callback(null, 'Failed to upload image');
        },
      });
    };
  }
}
